import React from "react";
import { Empty, List, Tag, Spin } from "antd";
import { FileTextOutlined, CalendarOutlined, EditOutlined, CheckCircleOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { baseService } from "~/services/base";
import { capitalize } from "~/utils";

type Activity = {
  id: string;
  type: "BOOKING_CREATED" | "SERVICE_SCHEDULED" | "BOOKING_UPDATED" | "STATUS_CHANGED";
  description: string;
  createdAt: string;
  jobTicket?: {
    id: string;
    referenceNumber: string;
    client?: {
      companyName?: string;
      firstName?: string;
      lastName?: string;
    };
  };
  serviceTicket?: {
    id: string;
    serial: string;
    serviceType: string;
  };
  user?: {
    firstName: string;
    lastName: string;
  };
  metadata?: Record<string, any>;
};

export const RecentActivity: React.FC = () => {
  const { data: activities, isLoading } = useQuery<Activity[]>({
    queryKey: ["activities", "recent"],
    queryFn: async () => {
      const { data } = await baseService.get("/activities/recent", {
        params: { limit: 5 },
      });
      return data;
    },
    refetchInterval: 60000, // Refetch every minute
  });

  const getActivityIcon = (type: string) => {
    switch (type) {
      case "BOOKING_CREATED":
        return <FileTextOutlined className="text-blue-500" />;
      case "SERVICE_SCHEDULED":
        return <CalendarOutlined className="text-green-500" />;
      case "BOOKING_UPDATED":
        return <EditOutlined className="text-orange-500" />;
      case "STATUS_CHANGED":
        return <CheckCircleOutlined className="text-purple-500" />;
      default:
        return <FileTextOutlined />;
    }
  };

  const getActivityColor = (type: string) => {
    switch (type) {
      case "BOOKING_CREATED":
        return "blue";
      case "SERVICE_SCHEDULED":
        return "green";
      case "BOOKING_UPDATED":
        return "orange";
      case "STATUS_CHANGED":
        return "purple";
      default:
        return "default";
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 60) return "just now";
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="pb-4">
      <h3 className="font-medium mb-4">Recent Activity</h3>
      {isLoading ? (
        <div className="flex justify-center py-8">
          <Spin />
        </div>
      ) : !activities || activities.length === 0 ? (
        <Empty
          description="No recent activity"
          className="py-8"
        />
      ) : (
        <List
          dataSource={activities}
          renderItem={(activity: Activity) => (
            <List.Item className="hover:bg-gray-50 transition-colors px-2 -mx-2 rounded">
              <List.Item.Meta
                avatar={
                  <div className="text-2xl">{getActivityIcon(activity.type)}</div>
                }
                title={
                  <div className="flex items-center gap-2">
                    <Tag color={getActivityColor(activity.type)}>
                      {capitalize(activity.type.replace(/_/g, " "))}
                    </Tag>
                    {activity.jobTicket && (
                      <Link
                        to={`/bookings/${activity.jobTicket.id}`}
                        className="text-blue-600 hover:text-blue-800 font-medium"
                      >
                        #{activity.jobTicket.referenceNumber}
                      </Link>
                    )}
                  </div>
                }
                description={
                  <div>
                    <div className="text-sm text-gray-900 mb-1">
                      {activity.description}
                    </div>
                    <div className="text-xs text-gray-500">
                      {formatTimeAgo(activity.createdAt)}
                      {activity.user && (
                        <span className="ml-2">
                          by {activity.user.firstName} {activity.user.lastName}
                        </span>
                      )}
                    </div>
                  </div>
                }
              />
            </List.Item>
          )}
        />
      )}
    </div>
  );
};
